"use client";

import { useEffect, useRef, useState } from "react";
import { useLenis } from "lenis/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reveal } from "@/components/ui/Reveal";
import { AnimatedBookCard } from "@/components/ui/AnimatedBookCard";

export type MasonryTextCard = {
  type: "text";
  quote: string;
  authorName: string;
  authorTitle: string;
  imageSrc: string;
  imageAlt: string;
};

export type MasonryVideoCard = {
  type: "video";
  youtubeId: string;
  quote: string;
  authorName: string;
  authorTitle: string;
  tall?: boolean;
};

export type MasonryCard = MasonryTextCard | MasonryVideoCard;

export type TestimonialsMasonrySectionProps = {
  eyebrow?: string;
  heading?: string;
  description?: string;
  columns: MasonryCard[][];
  /** Max-width utility for the inner container. Defaults to full-bleed (matches every existing usage). */
  maxWidthClassName?: string;
  className?: string;
};

const card3dShadow =
  "shadow-[0_2px_4px_rgba(41,54,26,0.04),0_8px_16px_rgba(41,54,26,0.08),0_20px_40px_rgba(41,54,26,0.12),0_32px_64px_rgba(41,54,26,0.08)]";

function bookTitleFromAuthorTitle(authorTitle: string, fallback: string) {
  return authorTitle.replace(/^Author of\s+/i, "").replace(/^"|"$/g, "") || fallback;
}

function ExpandableQuote({
  text,
  limit,
  expanded,
  onExpandedChange,
  className,
  dotsClassName,
}: {
  text: string;
  limit: number;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  className?: string;
  dotsClassName?: string;
}) {
  const needsTruncate = text.length > limit;

  const setExpanded = (next: boolean) => {
    onExpandedChange(next);
    // Defer ST refresh so the expand reflow isn't measured mid-commit.
    window.setTimeout(() => {
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }, 120);
  };

  if (!needsTruncate) {
    return (
      <p className={className}>
        &ldquo;{text}&rdquo;
      </p>
    );
  }

  if (expanded) {
    return (
      <p className={className}>
        &ldquo;{text}&rdquo;{" "}
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className={[
            "inline font-semibold underline-offset-2 hover:underline",
            dotsClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          Show less
        </button>
      </p>
    );
  }

  const truncated = text.slice(0, limit).replace(/\s+\S*$/, "").trimEnd();

  return (
    <p className={className}>
      &ldquo;{truncated}
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label="Continue reading full review"
        className={[
          "inline px-0.5 font-bold tracking-wide transition-opacity hover:opacity-70",
          dotsClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        ...
      </button>
      &rdquo;
    </p>
  );
}

function TextCard({
  card,
  expanded,
  onExpandedChange,
}: {
  card: MasonryTextCard;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  return (
    <article
      className={[
        "rounded-[1.35rem] border border-white/80 bg-white p-6 sm:p-7",
        "transition-[transform,box-shadow] duration-300",
        "hover:-translate-y-1 hover:shadow-[0_4px_8px_rgba(41,54,26,0.06),0_12px_24px_rgba(41,54,26,0.1),0_28px_56px_rgba(41,54,26,0.16)]",
        card3dShadow,
      ].join(" ")}
    >
      <ExpandableQuote
        text={card.quote}
        limit={220}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        className="text-[15px] leading-[1.7] text-[#3a3a3a] sm:text-base sm:leading-[1.75]"
        dotsClassName="text-[#b46c29]"
      />
      <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#eef1e8] pt-5">
        <div className="min-w-0 flex-1">
          <p className="font-heading text-base font-bold text-[#171717]">
            {card.authorName}
          </p>
          <p className="mt-0.5 text-sm leading-snug text-[#6b6b6b]">
            {card.authorTitle}
          </p>
        </div>
        <div className="relative z-20 -mr-1 shrink-0 overflow-visible">
          <AnimatedBookCard
            size="sm"
            imageSrc={card.imageSrc}
            imageAlt={card.imageAlt}
            title={bookTitleFromAuthorTitle(card.authorTitle, card.authorName)}
            author={card.authorName}
          />
        </div>
      </div>
    </article>
  );
}

function VideoCard({
  card,
  expanded,
  onExpandedChange,
}: {
  card: MasonryVideoCard;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const containerRef = useRef<HTMLElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [shouldPlay, setShouldPlay] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldPlay(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px 0px", threshold: 0.15 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const postToPlayer = (func: "mute" | "unMute") => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      "https://www.youtube.com",
    );
  };

  const handleToggleMute = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setMuted((prev) => {
      const next = !prev;
      postToPlayer(next ? "mute" : "unMute");
      return next;
    });
  };

  const embedSrc = `https://www.youtube.com/embed/${card.youtubeId}?autoplay=1&mute=1&controls=0&playsinline=1&loop=1&playlist=${card.youtubeId}&rel=0&modestbranding=1&enablejsapi=1`;
  const posterSrc = `/customer-reviews/yt-thumbs/${card.youtubeId}.webp`;

  return (
    <article
      ref={containerRef}
      className={[
        "overflow-hidden rounded-[1.35rem] bg-[#1a2210] ring-1 ring-black/10",
        "transition-[transform,box-shadow] duration-300 hover:-translate-y-1",
        expanded ? "flex flex-col" : "relative",
        !expanded &&
          (card.tall
            ? "min-h-[460px] sm:min-h-[540px]"
            : "min-h-[380px] sm:min-h-[430px]"),
        card3dShadow,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={
          expanded
            ? "relative h-[240px] w-full shrink-0 sm:h-[280px]"
            : "absolute inset-0"
        }
      >
        {shouldPlay ? (
          <iframe
            ref={iframeRef}
            title={`${card.authorName} video testimonial`}
            src={embedSrc}
            className="pointer-events-none absolute inset-0 h-full w-full scale-[1.4]"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${posterSrc})`,
            }}
            aria-hidden="true"
          />
        )}
        <div
          aria-hidden="true"
          className={
            expanded
              ? "absolute inset-0 bg-gradient-to-t from-[#1a2210] via-black/30 to-transparent"
              : "absolute inset-0 bg-gradient-to-t from-black/92 via-black/45 to-black/15"
          }
        />

        {shouldPlay ? (
          <button
            type="button"
            onClick={handleToggleMute}
            aria-label={muted ? "Unmute video" : "Mute video"}
            aria-pressed={!muted}
            className={[
              "absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full",
              "bg-black/45 text-white backdrop-blur-sm ring-1 ring-white/25",
              "transition-colors duration-200 hover:bg-black/65",
            ].join(" ")}
          >
            {muted ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="h-4.5 w-4.5"
                aria-hidden="true"
              >
                <path
                  d="M4 9v6h4l5 5V4L8 9H4Z"
                  fill="currentColor"
                />
                <path
                  d="M16.5 8.5 21 13m0-4.5-4.5 4.5"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="h-4.5 w-4.5"
                aria-hidden="true"
              >
                <path d="M4 9v6h4l5 5V4L8 9H4Z" fill="currentColor" />
                <path
                  d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        ) : null}
      </div>

      <div
        className={
          expanded
            ? "relative z-10 bg-[#1a2210] p-6 sm:p-7"
            : "absolute inset-x-0 bottom-0 z-10 p-6 sm:p-7"
        }
      >
        <ExpandableQuote
          text={card.quote}
          limit={150}
          expanded={expanded}
          onExpandedChange={onExpandedChange}
          className="text-[15px] leading-[1.65] text-white sm:text-base"
          dotsClassName="text-[#f5dcc8]"
        />
        <div className="mt-5 border-t border-white/15 pt-4">
          <p className="font-heading text-base font-bold text-white">
            {card.authorName}
          </p>
          <p className="mt-0.5 text-sm leading-snug text-white/75">
            {card.authorTitle}
          </p>
        </div>
      </div>
    </article>
  );
}

function Card({
  card,
  cardId,
  expandedId,
  onExpand,
}: {
  card: MasonryCard;
  cardId: string;
  expandedId: string | null;
  onExpand: (id: string | null) => void;
}) {
  const expanded = expandedId === cardId;
  const onExpandedChange = (next: boolean) => {
    onExpand(next ? cardId : null);
  };

  if (card.type === "video") {
    return (
      <VideoCard
        card={card}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
    );
  }

  return (
    <TextCard
      card={card}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    />
  );
}

const CARD_TRAVEL = [
  { from: 22, to: -26 },
  { from: 12, to: -14 },
  { from: 18, to: -22 },
] as const;

export function TestimonialsMasonrySection({
  eyebrow = "Voices of Success",
  heading = "Trusted by Authors Around the World",
  description = "Nothing speaks louder than the experiences of real authors. Watch their journeys & see how our end-to-end eBook publishing and author services have helped them publish with confidence and grow their readership.",
  columns,
  maxWidthClassName = "max-w-none",
  className,
}: TestimonialsMasonrySectionProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useLenis(() => {
    ScrollTrigger.update();
  });

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    let ctx: gsap.Context | null = null;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let cancelled = false;

    // Defer ScrollTrigger setup until after first paint / idle so GSAP's
    // geometry reads don't force reflow during LCP.
    const start = () => {
      if (cancelled) return;

      ctx = gsap.context(() => {
        const setup = (
          cards: HTMLElement[],
          getTravel: (index: number) => { from: number; to: number },
        ) => {
          cards.forEach((card, index) => {
            const travel = getTravel(index);

            gsap.fromTo(
              card,
              { yPercent: travel.from, force3D: true },
              {
                yPercent: travel.to,
                ease: "none",
                force3D: true,
                scrollTrigger: {
                  trigger: card,
                  start: "top bottom",
                  end: "bottom top",
                  scrub: 1.4,
                  invalidateOnRefresh: true,
                },
              },
            );
          });
        };

        const desktopCards = gridRef.current
          ? Array.from(
              gridRef.current.querySelectorAll<HTMLElement>(
                "[data-parallax-card]",
              ),
            )
          : [];

        setup(desktopCards, (index) => {
          const col = Number(
            desktopCards[index]?.getAttribute("data-col") ?? "0",
          );
          return CARD_TRAVEL[col] ?? CARD_TRAVEL[0];
        });

        const mobileCards = mobileRef.current
          ? Array.from(
              mobileRef.current.querySelectorAll<HTMLElement>(
                "[data-parallax-card]",
              ),
            )
          : [];

        setup(mobileCards, (index) => CARD_TRAVEL[index % 3] ?? CARD_TRAVEL[0]);
      }, sectionRef);

      // Single refresh after setup — avoid load-listener thrashing during LCP.
      requestAnimationFrame(() => ScrollTrigger.refresh());
    };

    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(start, { timeout: 1800 });
    } else {
      timeoutId = window.setTimeout(start, 200);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      ctx?.revert();
    };
  }, [columns]);

  return (
    <section
      ref={sectionRef}
      data-feather-section="testimonials"
      className={["bg-[#eef1e8] py-14 sm:py-16 lg:py-24", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={["mx-auto w-full px-4 sm:px-6 lg:px-8", maxWidthClassName].join(" ")}>
        <div className="mx-auto mb-10 max-w-4xl text-center lg:mb-12">
          <Reveal variant="zoom">
            <p className="font-heading text-xl font-semibold tracking-wide text-[#29361a] sm:text-[28px] sm:leading-9">
              {eyebrow}
            </p>
          </Reveal>

          <Reveal variant="zoom" delayMs={120}>
            <h2 className="mt-2 font-heading text-3xl font-bold text-[#b46c29] sm:text-4xl lg:text-[45px] lg:leading-[54px]">
              {heading}
            </h2>
          </Reveal>

          <Reveal variant="zoom" delayMs={220}>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-gray-700">
              {description}
            </p>
          </Reveal>
        </div>

        <div className="overflow-hidden rounded-[1.75rem]   px-3 pb-14 pt-10 sm:rounded-[2rem] sm:px-5 sm:pb-16 sm:pt-12 lg:px-7 lg:pb-20 lg:pt-14">
          <div ref={mobileRef} className="flex flex-col gap-6 md:hidden">
            {columns.flat().map((card, index) => {
              const cardId = `mobile-${card.authorName}-${index}`;
              return (
                <div
                  key={cardId}
                  data-parallax-card
                  className="will-change-transform"
                >
                  <Card
                    card={card}
                    cardId={cardId}
                    expandedId={expandedId}
                    onExpand={setExpandedId}
                  />
                </div>
              );
            })}
          </div>

          <div
            ref={gridRef}
            className="hidden gap-5 md:grid md:grid-cols-3 lg:gap-6"
          >
            {columns.map((column, columnIndex) => (
              <div
                key={`column-${columnIndex}`}
                className="flex flex-col gap-6 lg:gap-7"
                style={{
                  marginTop:
                    columnIndex === 1
                      ? "3rem"
                      : columnIndex === 2
                        ? "1rem"
                        : "0",
                }}
              >
                {column.map((card, cardIndex) => {
                  const cardId = `col-${columnIndex}-${card.authorName}-${cardIndex}`;
                  return (
                    <div
                      key={cardId}
                      data-parallax-card
                      data-col={columnIndex}
                      className="will-change-transform"
                    >
                      <Card
                        card={card}
                        cardId={cardId}
                        expandedId={expandedId}
                        onExpand={setExpandedId}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
